import { Injectable, signal } from '@angular/core';

export interface LoaderState {
  visible: boolean;
  deterministic: boolean;
  progress: number;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  private readonly requestCount = signal(0);

  readonly state = signal<LoaderState>({
    visible: false,
    deterministic: false,
    progress: 0,
  });

  show(options?: { deterministic?: boolean; progress?: number; message?: string }): void {
    this.requestCount.update((count) => count + 1);

    this.state.set({
      visible: true,
      deterministic: options?.deterministic ?? false,
      progress: this.normalizeProgress(options?.progress ?? 0),
      message: options?.message,
    });
  }

  hide(): void {
    this.requestCount.update((count) => Math.max(0, count - 1));

    if (this.requestCount() === 0) {
      this.state.update((state) => ({
        ...state,
        visible: false,
      }));
    }
  }

  setProgress(progress: number): void {
    this.state.update((state) => ({
      ...state,
      progress: this.normalizeProgress(progress),
    }));
  }

  setMessage(message?: string): void {
    this.state.update((state) => ({
      ...state,
      message,
    }));
  }

  reset(): void {
    this.requestCount.set(0);

    this.state.set({
      visible: false,
      deterministic: false,
      progress: 0,
    });
  }

  private normalizeProgress(progress: number): number {
    return Math.min(100, Math.max(0, progress));
  }
}
